<script setup>
import { fetchCurrentUserInfo } from '@/utils/userInfo.js'
import LocaleSwitch from '@/components/main/LocaleSwitch.vue' // 语言切换组件
import { parseHeziyunData } from '@/utils/heziyunData' // 获取php服务端渲染数据
import loading from '@/components/main/loading.vue' // 加载组件

// 菜单数据
const navItems = ref([])
const twoClassification = ref([])
const threeClass = ref([])

// 缓存
const threeLevelCache = new Map()
const heziyunDataCache = ref({
    headTypeNew: [],
    headType: [],
    head: []
})

const HeziyunData = ref(parseHeziyunData())

// 状态
const itemId = ref('')
const selectedBy = ref(null)
const productSelection = ref(null)
const showDropDownContent = ref(false)
const isMobile = ref(false)
const activeIndex = ref(null)

const underlineStyle = reactive({
    width: '0px',
    left: '0px'
})

// 用户信息
const userInfo = ref(null)

const undercut = ref(false)
const scrollPosition = ref(0)
const headerHeight = ref(0)

let hoverTimer = null
let headSelectTimer = null
let scrollRaf = null

const hoverDelay = 200
const secondHoverDelay = 200
const styles = ref(1)

// DOM 缓存
let navDomCache = []

// 当前一级下面的二级分类
const filteredClassifications = computed(() => {
    if (!itemId.value) return []
    return twoClassification.value.filter(item => item.types == itemId.value)
})

// 当前一级下面第一个二级ID
const recordTheFirstOne = computed(() => {
    return filteredClassifications.value[0]?.id || ''
})

// 所有二级ID
const allTwoLevelIds = computed(() => {
    return [...new Set(twoClassification.value.map(item => item.id))]
})

// 排序
const sortData = (data = []) => {
    return [...data].sort((a, b) => {
        const ar = Number(a.recommend == 1)
        const br = Number(b.recommend == 1)

        if (ar !== br) return br - ar
        if (ar && br) return Number(a.id) - Number(b.id)

        return 0
    })
}

// 建立二级分组索引
const twoLevelMap = computed(() => {
    const map = new Map()

    twoClassification.value.forEach(item => {
        const key = item.types
        if (!map.has(key)) map.set(key, [])
        map.get(key).push(item)
    })

    return map
})

// 建立三级分组索引
const threeLevelMap = computed(() => {
    const map = new Map()

        ; (heziyunDataCache.value.head || []).forEach(item => {
            const key = item.typeid
            if (!map.has(key)) map.set(key, [])
            map.get(key).push(item)
        })

    map.forEach((list, key) => {
        map.set(key, sortData(list))
    })

    return map
})

// 根据二级ID获取三级数据
const getThreeLevelList = (twoLevelId) => {
    return threeLevelMap.value.get(twoLevelId) || []
}

// 加载三级数据
const loadThreeLevelData = (twoLevelId) => {
    if (!twoLevelId) return

    if (threeLevelCache.has(twoLevelId)) {
        threeClass.value = threeLevelCache.get(twoLevelId)
        return
    }

    const list = getThreeLevelList(twoLevelId)
    threeLevelCache.set(twoLevelId, list)
    threeClass.value = list
}

// 预加载三级数据
const preloadThreeLevelData = (twoLevelId) => {
    if (!twoLevelId || threeLevelCache.has(twoLevelId)) return

    const list = getThreeLevelList(twoLevelId)
    threeLevelCache.set(twoLevelId, list)
}

// 批量预加载
const batchPreloadThreeLevelData = () => {
    const ids = allTwoLevelIds.value.slice(0, 10)

    if ('requestIdleCallback' in window) {
        requestIdleCallback(() => {
            ids.forEach(id => preloadThreeLevelData(id))
        })
    } else {
        setTimeout(() => {
            ids.forEach(id => preloadThreeLevelData(id))
        }, 100)
    }
}

// 头部样式
const headerStyle = computed(() => {
    if (filteredClassifications.value.length !== 0) {
        return { position: 'fixed' }
    }

    return {
        position: scrollPosition.value > headerHeight.value ? 'fixed' : 'absolute'
    }
})

// 用户信息请求
const userRequest = async () => {
    userInfo.value = await fetchCurrentUserInfo()
}

// 清除登录信息
const eliminate = () => {
    document.cookie.split(';').forEach(cookie => {
        const name = cookie.split('=')[0].trim()

        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
    })
    userInfo.value = null
}

// 获取导航DOM
const refreshNavDoms = () => {
    navDomCache = Array.from(document.querySelectorAll('.Heziyun-navjumpTo'))
}

// 清除导航激活
const clearNavActive = () => {
    navDomCache.forEach(el => el.classList.remove('Heziyun-active'))
}

// 更新下划线
const updateUnderlinePosition = (index) => {
    if (!navDomCache.length) refreshNavDoms()

    clearNavActive()

    const target = navDomCache[index]

    if (!target) return

    target.classList.add('Heziyun-active')

    underlineStyle.width = `${target.offsetWidth}px`
    underlineStyle.left = `${target.offsetLeft}px`
}

// 鼠标进入一级菜单
const handleMouseEnter = (index, item) => {
    clearTimeout(hoverTimer)

    hoverTimer = setTimeout(() => {
        itemId.value = item.id
        showDropDownContent.value = item.isJump !== 0
        activeIndex.value = index

        updateUnderlinePosition(index)

        if (item.isJump !== 0) {
            const relatedTwoLevels = twoLevelMap.value.get(item.id) || []
            relatedTwoLevels.forEach(twoItem => preloadThreeLevelData(twoItem.id))
        }
    }, hoverDelay)
}

// 鼠标离开一级菜单
const handleMouseLeaveitem = () => {
    clearTimeout(hoverTimer)
}

// 鼠标离开导航区域
const handleMouseLeave = () => {
    if (filteredClassifications.value.length === 0) {
        underlineStyle.width = '0px'
        underlineStyle.left = '0px'
    }

    clearNavActive()
}

// 真正选择二级菜单
const doHeadSelect = (id, style) => {
    if (!id) return

    selectedBy.value = id
    styles.value = style || filteredClassifications.value.find(item => item.id == id)?.style || 1

    loadThreeLevelData(id)
}

// 二级菜单鼠标进入，停留够时间才选中
const handleSecondMouseEnter = (id, style) => {
    if (!id) return

    clearTimeout(headSelectTimer)

    headSelectTimer = setTimeout(() => {
        doHeadSelect(id, style)
    }, secondHoverDelay)
}

// 二级菜单鼠标离开，没停够时间就不选中
const handleSecondMouseLeave = () => {
    clearTimeout(headSelectTimer)
}

// 点击二级菜单，立即选中
const headSelect = (id, style) => {
    clearTimeout(headSelectTimer)
    doHeadSelect(id, style)
}

// 移动端选择一级菜单
const selected = (id) => {
    itemId.value = id
    productSelection.value = id

    const relatedTwoLevels = twoLevelMap.value.get(id) || []

    if (relatedTwoLevels.length > 0) {
        headSelect(relatedTwoLevels[0].id, relatedTwoLevels[0].style)
    }

    relatedTwoLevels.forEach(item => preloadThreeLevelData(item.id))
}

// 打开/关闭移动端菜单
const threeCylinder = () => {
    isMobile.value = !isMobile.value

    if (!isMobile.value) {
        itemId.value = ''
        return
    }

    const firstNav = navItems.value[0]
    if (firstNav) selected(firstNav.id)
}

// 关闭下拉菜单
const closeDropdownMenu = () => {
    showDropDownContent.value = false
    activeIndex.value = null
    itemId.value = ''
    selectedBy.value = null
    threeClass.value = []

    clearTimeout(headSelectTimer)

    nextTick(() => {
        clearNavActive()
    })

    underlineStyle.width = '0px'
    underlineStyle.left = '0px'
}

// 滚动，使用 requestAnimationFrame 节流
const handleScroll = () => {
    if (scrollRaf) return

    scrollRaf = requestAnimationFrame(() => {
        scrollPosition.value =
            window.scrollY ||
            window.pageYOffset ||
            document.documentElement.scrollTop ||
            0

        scrollRaf = null
    })
}

// 初始化全部菜单数据
const loadAllData = async () => {
    const data = parseHeziyunData() || {}

    heziyunDataCache.value = {
        headTypeNew: data.headTypeNew || [],
        headType: data.headType || [],
        head: data.head || []
    }

    navItems.value = heziyunDataCache.value.headTypeNew
    twoClassification.value = sortData(heziyunDataCache.value.headType)

    productSelection.value = navItems.value[0]?.id || null

    if (twoClassification.value.length > 0) {
        batchPreloadThreeLevelData()
    }

    await nextTick()
    refreshNavDoms()
}

onMounted(async () => {
    userRequest()

    await loadAllData()

    headerHeight.value = document.getElementById('head')?.offsetHeight || 0

    window.addEventListener('scroll', handleScroll, { passive: true })
})

onBeforeUnmount(() => {
    window.removeEventListener('scroll', handleScroll)

    clearTimeout(hoverTimer)
    clearTimeout(headSelectTimer)

    if (scrollRaf) {
        cancelAnimationFrame(scrollRaf)
        scrollRaf = null
    }
})

// 一级变化后，自动选中第一个二级，并加载三级
watch(recordTheFirstOne, (newVal) => {
    if (!newVal) {
        selectedBy.value = null
        threeClass.value = []
        return
    }

    const firstTwo = filteredClassifications.value.find(item => item.id == newVal)
    headSelect(newVal, firstTwo?.style)
})

// 移动端打开时初始化第一个菜单
watch(isMobile, (newVal) => {
    if (newVal && navItems.value.length > 0) {
        selected(navItems.value[0].id)
    }
})
</script>

<template>
    <div class="Heziyun-header Heziyun-header-superclass" :style="headerStyle"
        :class="{ 'Heziyun-cutin': activeIndex == null && scrollPosition > headerHeight }">
        <div class="Heziyun-default" :class="{
            'Heziyun-integration': filteredClassifications.length !== 0
        }">

            <div class="Heziyun-logo" @mouseleave="handleMouseLeave()">
                <a href="/" v-intersect="{ class: 'opacity', delay: 0 }" class="Heziyun-imgContainer">
                    <img :src="HeziyunData.global?.logo1" :style="{ opacity: HeziyunData.global?.logo1 ? 1 : 0 }"
                        alt="" />
                </a>

                <a class="pc" :href="item.url ? item.url : '##'" :class="{
                    ['Heziyun-navjumpTo']: true,
                    'Heziyun-slideDownward': item.isJump != 0,
                }" v-for="(item, index) in navItems" :key="item.id" @mouseenter="handleMouseEnter(index, item)"
                    @mouseleave="handleMouseLeaveitem()" v-intersect="{ class: 'right-left', delay: index * 20 }">
                    {{ item.title }}
                    <div class="Heziyun-popular" v-if="item.recommend != 0">HOT</div>
                </a>

                <div class="Heziyun-underline pc" :style="underlineStyle"></div>
            </div>

            <div class="Heziyun-login">
                <LocaleSwitch></LocaleSwitch>

                <a href="/new_Smell.html?parent_id=2" class="Heziyun-console pc">{{ $t('common.documentationCenter') }}</a>
                <a href="/clientarea" class="Heziyun-console Heziyun-verticalSign pc">{{ $t('common.console') }}</a>
                <a href="/login" class="Heziyun-console pc" v-if="!userInfo">
                    {{ $t('common.signIn') }}
                </a>

                <div class="Heziyun-threeBars h5" @click="threeCylinder()">
                    <img src="/src/static/img/header/threeBars.svg" alt="" v-if="!isMobile">
                    <img src="/src/static/img/header/fork.png" alt="" v-else>
                </div>

                <a href="/login" class="Heziyun-defaultAvatar h5" v-if="userInfo">
                    <img src="/src/static/img/header/moren.svg" alt="" v-if="!userInfo.email">
                    <img v-else :src="`https://q1.qlogo.cn/g?b=qq&nk=${userInfo.email}&s=100`" alt="">
                    <span>{{ userInfo.username }}</span>
                </a>

                <div class="Heziyun-userInfo pc" v-if="userInfo" @mouseleave="undercut = false"
                    @mouseenter="undercut = true">
                    <div class="Heziyun-headPortrait">
                        <div v-if="!userInfo.email">{{ userInfo.username.charAt(0) }}</div>
                        <img v-if="userInfo.email" :src="`https://q1.qlogo.cn/g?b=qq&nk=${userInfo.email}&s=100`"
                            alt="">
                    </div>

                    <div class="Heziyun-letter">
                        <img src="/src/static/img/header/subscript.svg" alt="">
                    </div>

                    <transition name="fade">
                        <div class="Heziyun-informationDropdown" v-if="undercut">
                            <a href="/cart" class="Heziyun-item">
                                <img src="/src/static/img/header/cart1.svg" alt="">{{ $t('common.Cart') }}
                            </a>
                            <a href="/details" class="Heziyun-item">
                                <img src="/src/static/img/header/user.svg" alt="">{{ $t('common.userInformation') }}
                            </a>
                            <a href="/billing" class="Heziyun-item">
                                <img src="/src/static/img/header/cart.svg" alt="">{{ $t('common.unpaidOrders') }}
                            </a>
                            <a href="/supporttickets" class="Heziyun-item">
                                <img src="/src/static/img/header/gongdan.svg" alt="">{{ $t('common.myTicket') }}
                            </a>
                            <div class="Heziyun-item" @click="eliminate()">
                                <img src="/src/static/img/header/tui.svg" alt="">{{ $t('common.exitAccount') }}
                            </div>
                        </div>
                    </transition>
                </div>

                <a href="/regist" class="Heziyun-button2" v-else>
                    <img src="/src/static/img/header/liwu.svg" alt="">{{ $t('common.register') }}
                </a>
            </div>
        </div>

        <transition name="dropdown-fade" mode="out-in">
            <div class="Heziyun-dropDownContent pc" v-if="filteredClassifications.length !== 0" :style="{
                position: showDropDownContent ? 'fixed' : 'absolute'
            }">
                <div class="Heziyun-itemHoverContent">

                    <div class="Heziyun-leftMenu">
                        <div class="Heziyun-navigationList">
                            <div class="Heziyun-item" v-for="(item, index) in filteredClassifications" :key="item.id"
                                @mouseenter="handleSecondMouseEnter(item.id, item.style)"
                                @mouseleave="handleSecondMouseLeave" @click="headSelect(item.id, item.style)" :class="{
                                    'Heziyun-style1': item.id == selectedBy,
                                    'Heziyun-style2': item.style == 2 && item.id == selectedBy,
                                }" v-intersect="{ class: 'left-right', delay: index * 20 }">
                                {{ item.title }}
                                <div class="iconfont icon-youjiantou1"></div>
                                <div class="Heziyun-popular" v-if="item.recommend != 0">HOT</div>
                            </div>
                        </div>
                    </div>

                    <div class="Heziyun-rightMenu" :class="{ 'Heziyun-style2': styles == 2 }">
                        <ul>
                            <li v-for="(item, index) in threeClass" :key="item.id"
                                v-intersect="{ class: 'right-left', delay: index * 30 }">
                                <a :href="item.url">
                                    <span>
                                        {{ item.title }}
                                        <div class="iconfont icon-youjiantou1"></div>
                                    </span>
                                    <p>{{ item.tips }}</p>
                                    <div class="Heziyun-hide" v-if="item.recommend != 0">
                                        <div class="Heziyun-popular">{{ $t('common.selected') }}</div>
                                    </div>
                                </a>
                            </li>
                        </ul>
                    </div>

                </div>
            </div>
        </transition>

        <div class="Heziyun-closes" @click="closeDropdownMenu()" v-if="filteredClassifications.length !== 0">
            <img src="/src/static/img/header/fork.png" alt="" />
        </div>

        <transition name="opacity-fade">
            <div class="Heziyun-modalBox" @click="closeDropdownMenu()" v-if="filteredClassifications.length !== 0">
            </div>
        </transition>

        <transition name="transform-fade">
            <div class="Heziyun-moveDropdown h5" v-if="isMobile">

                <div class="Heziyun-primaryKey">
                    <div class="Heziyun-moveMenu">
                        <a :href="item.isJump == 1 ? '##' : item.url" class="Heziyun-itemMenu"
                            v-for="(item, index) in navItems" :key="item.id" :tabindex="index"
                            v-intersect="{ class: 'top-bottom', delay: index * 30 }" @click="selected(item.id)"
                            :class="{ 'Heziyun-Classe': item.id == productSelection ? '#fff' : '' }">
                            <div class="Heziyun-titles">
                                <span>{{ item.title }}</span>
                            </div>
                            <img v-if="item.isJump == 1" src="/src/static/img/header/youimg.png" alt=""
                                class="Heziyun-img">
                        </a>
                    </div>

                    <div class="Heziyun-moveMenuTow">
                        <transition name="transform-fade">

                            <div class="Heziyun-moveMenuTow-item Heziyun-giveBirthTo"
                                v-if="filteredClassifications.length !== 0">

                                <div class="Heziyun-tagList">
                                    <div class="Heziyun-itemTag" v-for="(item, index) in filteredClassifications"
                                        :key="item.id" v-intersect="{ class: 'right-left', delay: index * 50 }"
                                        @click="headSelect(item.id, item.style)" :style="{
                                            backgroundColor: item.id == selectedBy ? '#1677FF' : '#fff',
                                            color: item.id == selectedBy ? '#fff' : '#000'
                                        }">
                                        {{ item.title }}
                                    </div>
                                </div>

                                <div class="Heziyun-productList">
                                    <a :href="item.url" class="Heziyun-itemProduct" v-for="(item, index) in threeClass"
                                        :key="item.id" v-intersect="{ class: 'right-left', delay: index * 50 }">
                                        <span>{{ item.title }}</span>
                                        <p>{{ item.tips }}</p>
                                        <div class="Heziyun-hide" v-if="item.recommend != 0">
                                            <div class="Heziyun-popular">{{ $t('common.selected') }}</div>
                                        </div>
                                    </a>
                                </div>
                            </div>

                        </transition>
                    </div>
                </div>
            </div>
        </transition>

    </div>

    <loading :HeziyunData></loading>
</template>

<style lang="less">
// 声明
:root {
    --head-height: 3.6rem;
    --gradient-bg: linear-gradient(90deg, #ff8a00, #e52e71);
}

// 头部样式
.Heziyun-header {
    box-shadow: 0 4px 10px rgba(20, 100, 255, .1);
    height: var(--head-height);
    width: 100%;
    top: 0;
    left: 0;
    z-index: 1000;

    .Heziyun-default {
        display: flex;
        width: auto;
        padding: 0 0 0 3rem;
        justify-content: space-between;
        position: relative;
        z-index: 999;
        background-color: rgba(255, 255, 255, 0.631372549);
        backdrop-filter: blur(20px);
        font-family: '思源黑';

        &:hover {
            background-color: #fff;
        }

        // 左侧logo以及导航
        .Heziyun-logo {
            display: flex;
            align-items: center;
            height: var(--head-height);
            position: relative;

            .Heziyun-imgContainer {
                width: 180px;
                height: 3.6rem;

                img {
                    width: 100%;
                    height: 100%;
                    object-fit: contain;
                }
            }

            .Heziyun-navjumpTo {
                margin: 0 1.2rem;
                height: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                white-space: nowrap;
                border-bottom: 0.15rem solid transparent;
                transition: all 0.3s ease;
                -webkit-transition: all 0.3s ease;
                -moz-transition: all 0.3s ease;
                -ms-transition: all 0.3s ease;
                -o-transition: all 0.3s ease;
                position: relative;
                font-size: 0.84rem;

                .Heziyun-popular {
                    padding: 2px 5px;
                    box-shadow: 2px 2px 4px 0 rgba(55, 99, 170, 0.31);
                    background: #ff2626;
                    border-radius: 6px;
                    border-bottom-left-radius: 0;
                    color: #fff;
                    font-weight: bold;
                    font-style: normal;
                    font-size: 10px;
                    line-height: 12px;
                    font-family: "YouShe";
                    position: absolute;
                    top: 15%;
                    left: 100%;
                }
            }

            .Heziyun-navjumpTo.Heziyun-active {
                color: #4589fd;
                transition: all 0.3s ease;

                &::after {
                    transform: rotate(90deg);
                    -webkit-transform: rotate(90deg);
                    -moz-transform: rotate(90deg);
                    -ms-transform: rotate(90deg);
                    -o-transform: rotate(90deg);
                    background: url('@/static/img/header/subscriptBlue.png') no-repeat center;
                    background-size: cover;
                }
            }

            .Heziyun-slideDownward {
                position: relative;

                &::after {
                    content: "";
                    background: url('@/static/img/header/subscript.png') no-repeat center;
                    background-size: cover;
                    position: absolute;
                    right: -1rem;
                    transform: rotate(-90deg);
                    top: 1.4rem;
                    width: 0.8rem;
                    height: 0.8rem;
                    transition: all 0.3s ease;
                }
            }

            // 下划线
            .Heziyun-underline {
                position: absolute;
                bottom: 0;
                height: 0.1rem;
                background-color: #4589fd;
                transition: all 0.4s ease;
                -webkit-transition: all 0.4s ease;
                -moz-transition: all 0.4s ease;
                -ms-transition: all 0.4s ease;
                -o-transition: all 0.4s ease;
            }
        }

        // 右侧登录按钮
        .Heziyun-login {
            display: flex;
            align-items: center;
            gap: 1rem;

            .Heziyun-console {
                padding: 0.3rem 0.6rem;

                &:hover {
                    color: #0056ff;
                    background-color: #f1f1f1;
                }
            }

            .Heziyun-verticalSign {
                &::before {
                    right: -20%;
                }

                &::after {
                    left: -20%;
                }
            }

            .Heziyun-button2 {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 0.1rem;
                transition: all 0.2s ease-in;
                position: relative;
                overflow: hidden;
                z-index: 1;
                color: #fff;
                padding: 0 1.5rem;
                cursor: pointer;
                font-size: 0.85rem;
                background: #0056ff;
                border: 1px solid #e8e8e8;
                box-shadow: 0 0 12px #c5c5c5, -6px -6px 12px #ffffff;
                height: 100%;

                img {
                    width: 1.5rem;
                    height: 1.5rem;
                }
            }

            .Heziyun-userInfo {
                display: flex;
                align-items: center;
                gap: 0.5rem;
                cursor: pointer;
                position: relative;
                height: var(--head-height);
                padding-right: var(--head-height);
                padding-left: 1rem;
                position: relative;

                &:hover {

                    .Heziyun-headPortrait {
                        box-shadow: 8px 8px 20px 0 rgb(55 99 170 / 40%);
                    }

                    .Heziyun-letter {
                        span {
                            color: #4589fd;
                        }
                    }
                }

                .Heziyun-headPortrait {
                    border-radius: 9999px;
                    height: 2.5rem;
                    width: 2.5rem;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1rem;
                    color: #fff;
                    background: #0056ff;
                    border: 2px solid #fff;
                    box-shadow: 8px 8px 20px 0 rgb(55 99 170 / 40%);
                    position: relative;

                    &::after {
                        content: "";
                        position: absolute;
                        width: 0.4rem;
                        height: 0.4rem;
                        right: 0.05rem;
                        top: 0.2rem;
                        background-color: rgb(246, 64, 65);
                        border-radius: 50%;
                        -webkit-border-radius: 50%;
                        -moz-border-radius: 50%;
                        -ms-border-radius: 50%;
                        -o-border-radius: 50%;
                    }

                    img {
                        width: 100%;
                        height: 100%;
                        object-fit: contain;
                        border-radius: 50%;
                        -webkit-border-radius: 50%;
                        -moz-border-radius: 50%;
                        -ms-border-radius: 50%;
                        -o-border-radius: 50%;
                    }
                }

                .Heziyun-letter {
                    span {
                        color: #374151;
                    }


                }

                /* 定义淡入淡出动画 */
                .fade-enter-active,
                .fade-leave-active {
                    transition: opacity 0.5s;
                    -webkit-transition: opacity 0.5s;
                    -moz-transition: opacity 0.5s;
                    -ms-transition: opacity 0.5s;
                    -o-transition: opacity 0.5s;
                }

                .fade-enter,
                .fade-leave-to {
                    opacity: 0;
                }

                .Heziyun-informationDropdown {
                    position: absolute;
                    background: white;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
                    top: 100%;
                    right: 10%;
                    border-radius: 0;
                    -webkit-border-radius: 0;
                    -moz-border-radius: 0;
                    -ms-border-radius: 0;
                    -o-border-radius: 0;
                    overflow: hidden;

                    .Heziyun-item {
                        padding: 0.7rem 1rem;
                        width: 10rem;
                        display: flex;
                        align-items: center;
                        gap: 0.3rem;

                        img {
                            width: 0.9rem;
                            height: 0.9rem;
                        }

                        &:hover {
                            background: #e7eaf0;
                        }
                    }

                    .Heziyun-item:nth-child(2n + 1) {
                        border-top: 1px solid #e7eaf0;
                    }
                }

            }

            .Heziyun-threeBars {
                padding-right: 5vw;

                img {
                    width: 6vw;
                    height: 6vw;
                    padding: 0.5vw;
                }
            }

            .Heziyun-button {
                color: #fff;
                padding: 1.5vw 5vw;
                border-radius: 1vw;
                font-size: 4vw;
                background: linear-gradient(0deg, rgba(0, 0, 0, 0.001), rgba(0, 0, 0, 0.001)), #2468F2;
                box-shadow: 0rem 0.25rem 0.38rem -0.25rem rgba(0, 0, 0, 0.1), 0rem 0.63rem 0.94rem -0.19rem rgba(0, 0, 0, 0.1);
            }

            .Heziyun-button2:active {
                color: #666;
                box-shadow: inset 4px 4px 12px #c5c5c5, inset -4px -4px 12px #ffffff;
            }

            .Heziyun-button2:before {
                content: "";
                position: absolute;
                left: 50%;
                transform: translateX(-50%) scaleY(1) scaleX(1.25);
                top: 100%;
                width: 140%;
                height: 180%;
                background-color: rgba(0, 0, 0, 0.05);
                border-radius: 50%;
                display: block;
                transition: all 0.5s 0.1s cubic-bezier(0.55, 0, 0.1, 1);
                z-index: -1;
            }

            .Heziyun-button2:after {
                content: "";
                position: absolute;
                left: 55%;
                transform: translateX(-50%) scaleY(1) scaleX(1.45);
                top: 180%;
                width: 160%;
                height: 190%;
                background-color: #009087;
                border-radius: 50%;
                display: block;
                transition: all 0.5s 0.1s cubic-bezier(0.55, 0, 0.1, 1);
                z-index: -1;
            }

            .Heziyun-button2:hover {
                color: #ffffff;
                border: 1px solid #4D8BFF;
            }

            .Heziyun-button2:hover:before {
                top: -35%;
                background-color: #4D8BFF;
                transform: translateX(-50%) scaleY(1.3) scaleX(0.8);
            }

            .Heziyun-button2:hover:after {
                top: -45%;
                background-color: #4D8BFF;
                transform: translateX(-50%) scaleY(1.3) scaleX(0.8);
            }

        }
    }

    .Heziyun-integration {
        background-color: #fff;
        box-shadow: 8px 8px 20px 0 rgba(55, 99, 170, .1);
    }

    /* 进场（enter）动画 */
    .dropdown-fade-enter-active {
        animation: translation 0.4s ease;
        -webkit-animation: translation 0.4s ease;
    }

    /* 出场（leave）动画 */
    .dropdown-fade-leave-active {
        animation: quits 0.4s ease;
        -webkit-animation: quits 0.4s ease;
    }

    // pc下拉内容
    .Heziyun-dropDownContent {
        width: 80%;
        height: 100vh;
        background-color: #fff;
        position: absolute;
        left: 0;
        top: 0;
        z-index: 998;
        padding-top: var(--head-height);

        .Heziyun-itemHoverContent {
            display: flex;
            height: 100%;

            .Heziyun-leftMenu {
                width: 25%;
                height: 100%;
                background: url('@/static/img/header/side-bg.a725f079.jpg') right bottom no-repeat;
                background-size: 400px auto;
                position: relative;
                padding: 1.5rem;
                display: flex;
                flex-direction: column;
                align-items: flex-end;
                gap: 1rem;
                box-shadow: 8px 8px 20px 0 rgba(55, 99, 170, 0.1);

                .Heziyun-navigationList {
                    display: flex;
                    flex-direction: column;
                    padding-bottom: 1rem;
                    width: 70%;
                    overflow-y: auto;

                    // 滚动条蓝色
                    &::-webkit-scrollbar {
                        display: none;
                    }

                    &::-moz-scrollbar {
                        display: none;
                    }


                    .Heziyun-item {
                        background-color: transparent;
                        padding: 0.45rem 1rem;
                        cursor: pointer;
                        flex-shrink: 0;
                        border: 1.5px solid transparent;
                        color: #444;
                        position: relative;
                        display: inline-block;
                        font-size: 0.8rem;

                        .iconfont {
                            display: inline-block;
                            position: absolute;
                            right: 1rem;
                            top: 50%;
                            transform: translateY(-50%);
                            -webkit-transform: translateY(-50%);
                            -moz-transform: translateY(-50%);
                            -ms-transform: translateY(-50%);
                            -o-transform: translateY(-50%);
                            opacity: 0;
                        }

                        .Heziyun-popular {
                            padding: 2px 3px;
                            color: #fff;
                            font-weight: bold;
                            font-style: normal;
                            font-size: 8px;
                            line-height: 8px;
                            background: transparent;
                            border-radius: 2px;
                            border-color: #f64041;
                            color: #f64041;
                            border: 1px solid #f64041;
                            position: relative;
                            top: -2px;
                            margin-left: 5px;
                            display: inline-block;
                        }
                    }

                    .Heziyun-style1 {
                        background-color: rgb(241, 242, 245);
                        color: #1664FF;
                    }

                    .Heziyun-style2 {
                        color: #3860f4;
                        font-weight: 600;
                        background-color: rgb(235, 241, 253);
                        border-radius: 0.4rem;
                        -webkit-border-radius: 0.4rem;
                        -moz-border-radius: 0.4rem;
                        -ms-border-radius: 0.4rem;
                        -o-border-radius: 0.4rem;
                        padding-right: 2.5rem;

                        .iconfont {
                            opacity: 1;
                        }
                    }
                }

                .Heziyun-drawDescription {
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;

                    span {
                        color: rgb(53, 53, 56);
                        font-size: 0.95rem;
                    }

                    p {
                        color: rgb(98, 98, 99);
                        line-height: 1.5rem;
                        font-size: 0.85rem;
                    }
                }
            }

            // 右侧菜单内容样式一
            .Heziyun-rightMenu {
                overflow: hidden;
                padding: 1.5rem;
                width: 75%;
                height: 100%;
                background-color: #f8f8f8;

                // 滚动条蓝色
                &::-webkit-scrollbar {
                    width: 0.6rem;
                }

                &::-webkit-scrollbar-thumb {
                    background: #adadad;
                }

                ul {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(18rem, 1fr));
                    gap: 1rem;


                    li {
                        text-overflow: ellipsis;
                        white-space: nowrap;
                        display: block;
                        background-image: linear-gradient(180deg, #f3f5f8, #fff);
                        border: 2px solid #fff;
                        box-shadow: 8px 8px 20px rgba(55, 99, 170, .1), -8px -8px 20px #fff;
                        position: relative;
                        background: url('@/static/img/header/bg_3ac.jpg') no-repeat center;
                        background-size: cover;
                        position: relative;

                        &:hover {
                            a {
                                span {
                                    color: #1664FF;
                                }
                            }
                        }

                        a {
                            padding: 1rem;
                            display: flex;
                            flex-direction: column;
                            gap: 0.4rem;


                            span {
                                color: #444;
                                position: relative;
                                z-index: 1;
                                overflow: hidden;
                                text-overflow: ellipsis;
                                white-space: nowrap;
                                display: block;
                                min-height: 1.4rem;

                                .iconfont {
                                    opacity: 0;
                                    display: none;
                                }
                            }

                            p {
                                color: rgba(34, 34, 34, 0.5);
                                font-size: 0.8rem;
                                position: relative;
                                z-index: 1;
                                overflow: hidden;
                                text-overflow: ellipsis;
                                white-space: nowrap;
                                display: block;
                            }

                            .Heziyun-hide {
                                width: 74px;
                                height: 74px;
                                position: absolute;
                                right: -7px;
                                top: -5px;
                                overflow: hidden;
                                -webkit-font-smoothing: auto;

                                .Heziyun-popular {
                                    width: 80px;
                                    height: 20px;
                                    color: #fff;
                                    right: -19px;
                                    top: 11px;
                                    font-size: 12px;
                                    line-height: 20px;
                                    text-align: center;
                                    background-color: #ff2626;
                                    position: absolute;
                                    -webkit-transform: rotate(45deg);
                                    transform: rotate(45deg);

                                    &::before {
                                        content: "";
                                        left: 0;
                                        top: 17px;
                                        width: 5px;
                                        height: 6px;
                                        background-color: #ff6000;
                                        position: absolute;
                                        -webkit-transform: rotate(45deg);
                                        transform: rotate(45deg);
                                    }

                                    &::after {
                                        content: "";
                                        width: 6px;
                                        height: 5px;
                                        bottom: -2px;
                                        right: 0;
                                        background-color: #ff6000;
                                        position: absolute;
                                        -webkit-transform: rotate(45deg);
                                        transform: rotate(45deg);
                                    }
                                }
                            }
                        }
                    }
                }
            }

            // 右侧菜单内容样式二
            .Heziyun-style2 {
                ul {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 1rem;

                    li {
                        overflow: initial;
                        text-overflow: initial;
                        white-space: initial;
                        background: #00000000;
                        border-radius: 0.8rem;
                        -webkit-border-radius: 0.8rem;
                        -moz-border-radius: 0.8rem;
                        -ms-border-radius: 0.8rem;
                        -o-border-radius: 0.8rem;
                        border: 2px solid #fff;

                        &::after {
                            content: "";
                            position: absolute;
                            top: 0;
                            left: 0;
                            width: 100%;
                            height: 100%;
                            background: url('@/static/img/header/bg4ac.jpg') no-repeat center;
                            background-size: cover;
                            transition: all 0.3s ease;
                            -webkit-transition: all 0.3s ease;
                            -moz-transition: all 0.3s ease;
                            -ms-transition: all 0.3s ease;
                            -o-transition: all 0.3s ease;
                            z-index: -1;
                            border-radius: 0.8rem;
                            -webkit-border-radius: 0.8rem;
                            -moz-border-radius: 0.8rem;
                            -ms-border-radius: 0.8rem;
                            -o-border-radius: 0.8rem;
                        }

                        &:hover {
                            background: transparent;

                            a {

                                span {
                                    .iconfont {
                                        opacity: 1;
                                        display: block;
                                    }
                                }
                            }
                        }

                        a {
                            min-height: 6rem;

                            span {
                                color: #16213a;
                                display: flex;
                                align-items: center;
                                justify-content: space-between;
                                font-size: 0.9rem;
                            }

                            p {
                                display: -webkit-box;
                                -webkit-line-clamp: 2;
                                -webkit-box-orient: vertical;
                                overflow: hidden;
                                text-overflow: ellipsis;
                                white-space: initial;
                                font-size: 0.75rem;
                                line-height: 1.8;
                            }
                        }
                    }
                }
            }
        }
    }

    // 关闭按钮
    .Heziyun-closes {
        position: absolute;
        right: 2rem;
        top: 6rem;
        cursor: pointer;
        background-color: #fff;
        border-radius: 50%;
        -webkit-border-radius: 50%;
        -moz-border-radius: 50%;
        -ms-border-radius: 50%;
        -o-border-radius: 50%;
        z-index: 998;
        padding: 0.5rem;

        &:hover {
            background-color: #ffffff93;
        }

        img {
            width: 1.5rem;
            height: 1.5rem;
        }
    }

    /* 进场（enter）动画 */
    .opacity-fade-enter-active {
        animation: opacity 0.4s ease;
        -webkit-animation: opacity 0.4s ease;
    }

    /* 出场（leave）动画 */
    .opacity-fade-leave-active {
        animation: quitOpacity 0.4s ease;
        -webkit-animation: quitOpacity 0.4s ease;
    }

    // 模态框
    .Heziyun-modalBox {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.6);
        z-index: 997;
    }


}

@keyframes cutAnimation {
    0% {
        transform: translateY(-100%);
        -webkit-transform: translateY(-100%);
        -moz-transform: translateY(-100%);
        -ms-transform: translateY(-100%);
        -o-transform: translateY(-100%);
    }

    100% {
        transform: translateY(0);
        -webkit-transform: translateY(0);
        -moz-transform: translateY(0);
        -ms-transform: translateY(0);
        -o-transform: translateY(0);

    }
}

.Heziyun-cutin {
    animation: cutAnimation 0.4s ease-out;
    -webkit-animation: cutAnimation 0.4s ease-out;
}

@media (max-width: 768px) {
    .Heziyun-header {
        height: 15vw;

        .Heziyun-default {
            padding: 0 0 0 5vw;
            height: 100%;

            // 左侧logo以及导航
            .Heziyun-logo {
                height: 100%;

                .Heziyun-imgContainer {
                    width: 35vw;
                    height: 15vw;
                }

                img {
                    width: 35vw;
                    object-fit: contain;
                }
            }

            // 右侧登录按钮
            .Heziyun-login {
                gap: 0;

                .Heziyun-console {
                    padding: 1.5vw 4vw;
                    font-size: 3.5vw;
                    border-radius: 1vw;
                    -webkit-border-radius: 1vw;
                    -moz-border-radius: 1vw;
                    -ms-border-radius: 1vw;
                    -o-border-radius: 1vw;
                }

                .Heziyun-defaultAvatar {
                    padding-right: 5vw;
                    display: flex !important;
                    align-items: center;
                    gap: 1vw;

                    img {
                        width: 9vw;
                        height: 9vw;
                        border-radius: 50%;
                        -webkit-border-radius: 50%;
                        -moz-border-radius: 50%;
                        -ms-border-radius: 50%;
                        -o-border-radius: 50%;
                        object-fit: contain;
                    }

                    span {
                        overflow: hidden;
                        white-space: nowrap;
                        text-overflow: ellipsis;
                        width: 20vw;
                        display: inline-block;
                    }
                }

                .Heziyun-button {
                    color: #fff;
                    padding: 1.5vw 5vw;
                    border-radius: 1vw;
                    font-size: 4vw;
                    background: linear-gradient(0deg, rgba(0, 0, 0, 0.001), rgba(0, 0, 0, 0.001)), #2468F2;
                    box-shadow: 0rem 0.25rem 0.38rem -0.25rem rgba(0, 0, 0, 0.1), 0rem 0.63rem 0.94rem -0.19rem rgba(0, 0, 0, 0.1);
                }

                .Heziyun-button2 {
                    padding: 1.5vw 4vw;
                    font-size: 3.8vw;

                    img {
                        width: 6vw;
                        height: 6vw;
                    }
                }

                .Heziyun-button2:active {
                    color: #666;
                    box-shadow: inset 4px 4px 12px #c5c5c5, inset -4px -4px 12px #ffffff;
                }

                .Heziyun-button2:before {
                    content: "";
                    position: absolute;
                    left: 50%;
                    transform: translateX(-50%) scaleY(1) scaleX(1.25);
                    top: 100%;
                    width: 140%;
                    height: 180%;
                    background-color: rgba(0, 0, 0, 0.05);
                    border-radius: 50%;
                    display: block;
                    transition: all 0.5s 0.1s cubic-bezier(0.55, 0, 0.1, 1);
                    z-index: -1;
                }

                .Heziyun-button2:after {
                    content: "";
                    position: absolute;
                    left: 55%;
                    transform: translateX(-50%) scaleY(1) scaleX(1.45);
                    top: 180%;
                    width: 160%;
                    height: 190%;
                    background-color: #009087;
                    border-radius: 50%;
                    display: block;
                    transition: all 0.5s 0.1s cubic-bezier(0.55, 0, 0.1, 1);
                    z-index: -1;
                }

                .Heziyun-button2:hover {
                    color: #ffffff;
                    border: 1px solid #4D8BFF;
                }

                .Heziyun-button2:hover:before {
                    top: -35%;
                    background-color: #4D8BFF;
                    transform: translateX(-50%) scaleY(1.3) scaleX(0.8);
                }

                .Heziyun-button2:hover:after {
                    top: -45%;
                    background-color: #4D8BFF;
                    transform: translateX(-50%) scaleY(1.3) scaleX(0.8);
                }

            }
        }
    }

    /* 进场（enter）动画 */
    .transform-fade-enter-active {
        animation: transform-top-bottom 0.4s ease;
        -webkit-animation: transform-top-bottom 0.4s ease;
    }

    /* 出场（leave）动画 */
    .transform-fade-leave-active {
        animation: transform-bottom-top 0.4s ease;
        -webkit-animation: transform-bottom-top 0.4s ease;
    }

    // 移动下拉
    .Heziyun-moveDropdown {
        position: fixed;
        top: 15vw;
        left: 0;
        width: 100%;
        height: calc(100% - 15vw);
        background-color: #fff;
        z-index: 1100;
        overflow-y: scroll;
        overflow-x: hidden;

        // 关闭下拉
        .Heziyun-closeDropdownMenu {
            background-color: #fff;
            position: relative;
            display: flex;
            justify-content: space-between;
            padding: 3vw 5vw;

            .Heziyun-return {
                width: 7vw;
                height: 7vw;
                padding: 1vw;

                img {
                    width: 100%;
                    height: 100%;
                }
            }

            span {
                color: #000000;
                font-size: 5vw;
            }
        }

        .Heziyun-userInfo {
            padding: 2vw 5vw;
            display: flex !important;
            align-items: center;
            gap: 2vw;
            background: #fff;

            .Heziyun-headPortrait {
                border-radius: 9999px;
                height: 12vw;
                width: 12vw;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 5vw;
                border: 1.5px solid transparent;
                color: #2563EB;
                background: #DBEAFE;

                img {
                    width: 100%;
                    height: 100%;
                    object-fit: contain;
                    border-radius: 50%;
                    -webkit-border-radius: 50%;
                    -moz-border-radius: 50%;
                    -ms-border-radius: 50%;
                    -o-border-radius: 50%;
                }
            }

            .Heziyun-letter {
                position: relative;

                /* 定义淡入淡出动画 */
                .fade-enter-active,
                .fade-leave-active {
                    transition: opacity 0.5s;
                    -webkit-transition: opacity 0.5s;
                    -moz-transition: opacity 0.5s;
                    -ms-transition: opacity 0.5s;
                    -o-transition: opacity 0.5s;
                }

                .fade-enter,
                .fade-leave-to {
                    opacity: 0;
                }

                span {
                    color: #374151;
                }

                .Heziyun-informationDropdown {
                    position: absolute;
                    background: white;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
                    bottom: -800%;
                    width: 35vw;
                    right: 0%;
                    border-radius: 2vw;
                    -webkit-border-radius: 2vw;
                    -moz-border-radius: 2vw;
                    -ms-border-radius: 2vw;
                    -o-border-radius: 2vw;
                    overflow: hidden;
                    z-index: 11;

                    .Heziyun-item {
                        padding: 3vw;
                        display: flex;
                        align-items: center;
                        gap: 2vw;
                        font-size: 3.5vw;

                        img {
                            width: 4vw;
                            height: 4vw;
                        }

                        &:hover {
                            background: #e7eaf0;
                        }
                    }

                    .Heziyun-item:last-child {
                        border-top: 1px solid #e7eaf0;
                    }
                }
            }
        }

        // 搜索功能
        .Heziyun-search {
            background-color: #fff;
            position: relative;
            padding: 5vw;
            border-width: 0rem 0rem 0.06rem 0rem;
            border-style: solid;
            border-color: #F3F4F6;

            img {
                position: absolute;
                top: 50%;
                left: 9vw;
                transform: translateY(-50%);
                width: 4vw;
                height: 4vw;
            }

            input {
                background: #F9FAFB;
                width: 100%;
                height: 11vw;
                padding: 0 5vw 0 10vw;
                border-radius: 2vw;
                -webkit-border-radius: 2vw;
                -moz-border-radius: 2vw;
                -ms-border-radius: 2vw;
                -o-border-radius: 2vw;
                border: 1px solid transparent;

                &::placeholder {
                    font-size: 3.5vw;
                    color: #9CA3AF;
                }

                &:focus {
                    border: 1px solid rgb(109 173 253);
                }
            }
        }

        // 菜单
        .Heziyun-primaryKey {
            position: relative;
            display: flex;
            align-items: center;

            // 菜单一
            .Heziyun-moveMenu {
                display: flex;
                flex-direction: column;
                position: relative;
                flex: 0.3;
                width: 30%;
                background-color: rgb(245, 244, 247);
                height: calc(100vh - 15vw);
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;

                .Heziyun-itemMenu {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 3.5vw 3vw;

                    .Heziyun-titles {
                        display: flex;
                        align-items: center;
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        width: 20vw;
                        flex-shrink: 0;

                        span {
                            color: #1f2937;
                            font-size: 4vw;
                            font-family: "思源黑";
                            white-space: nowrap;
                            overflow: hidden;
                            text-overflow: ellipsis;
                        }
                    }

                    .Heziyun-img {
                        width: 4vw;
                        height: 4vw;
                        flex-shrink: 0;
                    }
                }

                .Heziyun-Classe {
                    background-color:rgb(149 184 255 / 18%);
                    border-right: 2px solid #0056ff;
                }
            }

            // 菜单二
            .Heziyun-moveMenuTow {
                flex: 0.7;
                width: 70%;
                height: calc(100vh - 15vw);
                overflow-y: auto;

                .Heziyun-moveMenuTow-item {
                    background-color: #fff;
                    width: 100%;
                    height: calc(100vh - 15vw);
                }

                // 产品中心
                .Heziyun-giveBirthTo {
                    .Heziyun-tagList {
                        padding: 5vw;
                        display: flex;
                        align-items: center;
                        gap: 2vw;
                        overflow-x: scroll;

                        &::-webkit-scrollbar {
                            display: none;
                        }

                        .Heziyun-itemTag {
                            flex-shrink: 0;
                            font-size: 4vw;
                            padding: 1.5vw 4vw;
                            border-radius: 10vw;
                            -webkit-border-radius: 10vw;
                            -moz-border-radius: 10vw;
                            -ms-border-radius: 10vw;
                            -o-border-radius: 10vw;
                            border: 1px solid #E5E7EB;
                        }
                    }

                    .Heziyun-productList {
                        //padding: 4vw;
                        margin: 2vw 5vw;
                        border-radius: 1vw;
                        -webkit-border-radius: 1vw;
                        -moz-border-radius: 1vw;
                        -ms-border-radius: 1vw;
                        -o-border-radius: 1vw;
                        display: grid;
                        grid-template-columns: repeat(1, 1fr);
                        gap: 2vw;
                        position: relative;

                        .Heziyun-hide {
                            width: 74px;
                            height: 74px;
                            position: absolute;
                            right: -7px;
                            top: -5px;
                            overflow: hidden;
                            -webkit-font-smoothing: auto;

                            .Heziyun-popular {
                                width: 80px;
                                height: 20px;
                                color: #fff;
                                right: -17px;
                                top: 11px;
                                font-size: 12px;
                                line-height: 20px;
                                text-align: center;
                                background-color: #ff2626;
                                position: absolute;
                                -webkit-transform: rotate(45deg);
                                transform: rotate(45deg);

                                &::before {
                                    content: "";
                                    left: 0;
                                    top: 17px;
                                    width: 5px;
                                    height: 6px;
                                    background-color: #ff6000;
                                    position: absolute;
                                    -webkit-transform: rotate(45deg);
                                    transform: rotate(45deg);
                                }

                                &::after {
                                    content: "";
                                    width: 6px;
                                    height: 5px;
                                    bottom: -2px;
                                    right: 0;
                                    background-color: #ff6000;
                                    position: absolute;
                                    -webkit-transform: rotate(45deg);
                                    transform: rotate(45deg);
                                }
                            }
                        }

                        .Heziyun-itemProduct {
                            background: url('@/static/img/header/bg_3ac.jpg') no-repeat center;
                            background-size: cover;
                            padding: 4vw;
                            border-radius: 2vw;
                            display: flex;
                            flex-direction: column;
                            gap: 1vw;
                            border: 1px solid #d6d6d6;
                            position: relative;


                            span {
                                font-size: 4vw;
                                position: relative;
                                z-index: 1;
                                white-space: nowrap;
                                overflow: hidden;
                                text-overflow: ellipsis;
                                width: 51vw;
                            }

                            p {
                                font-size: 3.5vw;
                                color: rgb(159, 167, 179);
                                line-height: 6vw;
                                position: relative;
                                z-index: 1;
                                white-space: nowrap;
                                overflow: hidden;
                                text-overflow: ellipsis;
                                width: 51vw;
                            }
                        }
                    }
                }
            }
        }

    }
}
</style>
